import React, { useEffect, useMemo, useState } from "react";

import type { WhisperEvent } from "./timelineWhisper";

type EtchedVisualPhase = "idle" | "enter" | "hold" | "exit";

const EXIT_DURATION_MS = 220;

function severityTone(event: WhisperEvent | null) {
  switch (event?.severity) {
    case "warning":
      return {
        accent: "rgba(251,191,36,0.82)",
        text: "rgba(255,247,211,0.9)",
        glow: "rgba(252,211,77,0.12)",
      };
    case "mode":
      return {
        accent: "rgba(94,234,212,0.84)",
        text: "rgba(226,255,250,0.9)",
        glow: "rgba(45,212,191,0.12)",
      };
    default:
      return {
        accent: "rgba(125,211,252,0.78)",
        text: "rgba(232,248,255,0.86)",
        glow: "rgba(56,189,248,0.1)",
      };
  }
}

function normalizeWhisperText(text: string): string {
  const clean = String(text ?? "").trim();
  if (!clean) return "";

  return clean
    .replace(/^Selection:\s*/i, "Selection · ")
    .replace(/^Audition mode:\s*/i, "Audition · ")
    .replace(/^FX node updated:\s*/i, "FX node updated · ")
    .replace(/^FX node added:\s*/i, "FX node added · ")
    .replace(/^ARC segment added$/i, "ARC segment added")
    .replace(/^ARC segment updated$/i, "ARC segment updated");
}

function splitInscription(text: string): { prefix: string; suffix: string } {
  const normalized = normalizeWhisperText(text);
  const separatorIndex = normalized.indexOf("·");
  if (separatorIndex === -1) {
    return {
      prefix: normalized,
      suffix: "",
    };
  }

  return {
    prefix: normalized.slice(0, separatorIndex).trim(),
    suffix: normalized.slice(separatorIndex + 1).trim(),
  };
}

function isLightModeActive(): boolean {
  if (typeof document === "undefined") return false;

  const root = document.documentElement;
  const body = document.body;
  if (root.classList.contains("dark") || body?.classList.contains("dark")) return false;

  if (typeof window !== "undefined" && typeof window.matchMedia === "function") {
    return !window.matchMedia("(prefers-color-scheme: dark)").matches;
  }

  return true;
}

export default function EtchedContextLine({
  event,
  retainedText,
}: {
  event: WhisperEvent | null;
  retainedText?: string | null;
}): JSX.Element | null {
  const [displayEvent, setDisplayEvent] = useState<WhisperEvent | null>(event);
  const [phase, setPhase] = useState<EtchedVisualPhase>(event ? "hold" : "idle");

  useEffect(() => {
    if (event) {
      setDisplayEvent(event);
      setPhase("enter");

      const frame = window.requestAnimationFrame(() => {
        setPhase("hold");
      });

      return () => window.cancelAnimationFrame(frame);
    }

    if (!displayEvent) {
      setPhase("idle");
      return undefined;
    }

    setPhase("exit");
    const timeoutId = window.setTimeout(() => {
      setDisplayEvent(null);
      setPhase("idle");
    }, EXIT_DURATION_MS);

    return () => window.clearTimeout(timeoutId);
  }, [displayEvent, event]);

  const activeText = displayEvent ? normalizeWhisperText(displayEvent.text) : "";
  const inscription = displayEvent ? activeText : String(retainedText ?? "").trim();
  const isTransient = !!displayEvent;
  const tone = useMemo(() => severityTone(displayEvent), [displayEvent]);
  const textParts = useMemo(() => splitInscription(inscription), [inscription]);
  const isLightMode = isLightModeActive();

  if (!inscription) return null;

  const transientOpacity = phase === "enter" ? 0.32 : phase === "hold" ? 1 : phase === "exit" ? 0 : 0;
  const retainedOpacity = retainedText ? (isLightMode ? 0.78 : 0.2) : 0;
  const opacity = isTransient ? transientOpacity : retainedOpacity;
  const transform = isTransient
    ? phase === "enter"
      ? "translateY(-3px)"
      : phase === "exit"
        ? "translateY(-1px)"
        : "translateY(0)"
    : "translateY(0)";
  const retainedPalette = isLightMode
    ? {
        text: "rgba(8,145,178,0.98)",
        prefix: "rgba(14,165,233,1)",
        suffix: "rgba(8,47,73,0.96)",
        textShadow: "0 0 10px rgba(56,189,248,0.34), 0 0 24px rgba(14,165,233,0.18)",
      }
    : {
        text: "rgba(219,234,254,0.34)",
        prefix: "rgba(125,211,252,0.28)",
        suffix: "rgba(232,248,255,0.28)",
        textShadow: "0 0 8px rgba(125,211,252,0.04)",
      };

  return (
    <div
      data-oc-etched-context-line="1"
      className="pointer-events-none relative flex h-8 items-center justify-center overflow-hidden"
      aria-live="polite"
      role="status"
    >
      <div
        className="relative max-w-[min(68vw,720px)] px-5 text-center transition-[opacity,transform,filter] duration-200 ease-out"
        style={{
          opacity,
          transform,
          filter: isTransient ? "blur(0px)" : "blur(0.15px)",
        }}
      >
        {isTransient ? (
          <div
            aria-hidden
            className="absolute inset-x-0 top-1/2 h-5 -translate-y-1/2 blur-md"
            style={{
              background: `radial-gradient(circle, ${tone.glow} 0%, transparent 72%)`,
            }}
          />
        ) : null}

        <div
          className="relative whitespace-nowrap text-[11px] font-light uppercase tracking-[0.24em]"
          style={{
            color: isTransient ? tone.text : retainedPalette.text,
            textShadow: isTransient ? `0 0 14px ${tone.glow}` : retainedPalette.textShadow,
          }}
          title={inscription}
        >
          <span style={{ color: isTransient ? tone.accent : retainedPalette.prefix }}>{textParts.prefix}</span>
          {textParts.suffix ? <span style={{ color: isTransient ? tone.text : retainedPalette.suffix }}>{` · ${textParts.suffix}`}</span> : null}
        </div>
      </div>
    </div>
  );
}